import { getAppHref } from "@/lib/env";
import {
  analysisEntryParams,
  analysisSignupHref,
  createAnalysisEntry,
  readAnalysisEntry,
  type AnalysisCtaId,
  type AnalysisEntryContext,
  type AnalysisLanding,
  type AnalysisLocale,
} from "@appcaire/shared/analysis/analysisEntry";
export { analysisOfferHref } from "@appcaire/shared/analysis/analysisEntry";

/**
 * Where a free-analysis start click sends the visitor: the app's sign-up,
 * the last step of the funnel the website itself can observe.
 */
export const ANALYSIS_SIGNUP_TARGET = "/sign-up";

export function analysisOfferLabel(locale: AnalysisLocale): string {
  return locale === "sv"
    ? "Analysera er besöksplanering kostnadsfritt"
    : "Get a complimentary visit planning analysis";
}

/** Navigation uses a bounded page family rather than arbitrary URLs in the funnel. */
export function analysisLandingForPath(path: string): AnalysisLanding {
  if (path === "/" || path === "/en") return "home";
  if (
    /\/resurser\/nyheter(?:\/|$)|\/resources\/news(?:\/|$)/.test(path) ||
    /\/artiklar\/(gratis-schemaanalys-sa-fungerar-det|ai-assistent-mcp)$|\/articles\/(free-schedule-analysis-how-it-works|ai-assistant-mcp)$/.test(
      path,
    )
  )
    return "news";
  if (/^\/(gratis-schemaanalys|en\/free-schedule-analysis)\/?$/.test(path))
    return "offer";
  if (/prissattning|pricing|kalkylator|calculator/.test(path)) return "pricing";
  if (/jamforelser|comparisons|excel-vs|carefox-vs/.test(path))
    return "comparison";
  if (/guider|guides/.test(path)) return "guide";
  if (/hjalp|help|vanliga-fragor|faq|boka-demo|book-demo|onboarding/.test(path))
    return "help";
  if (/losningar|solutions/.test(path)) return "persona";
  if (/ruttoptimering|route-optimization/.test(path)) return "routing";
  if (/analysverktyg|analytics/.test(path)) return "analytics";
  if (/ai-chat/.test(path)) return "ai_chat";
  if (/schemalaggning|scheduling|personalplanering|staff-planning/.test(path))
    return "scheduling";
  return "other";
}

const STORAGE_KEY = "caire.websiteAnalysisEntry";
let unavailableStorageContext: AnalysisEntryContext | null = null;

/** Store only the shared allowlist; neither referrer URLs nor campaign text survive. */
export function captureAnalysisEntry(
  locale: AnalysisLocale,
  landing: AnalysisLanding,
  cta: AnalysisCtaId,
  search: string,
): AnalysisEntryContext {
  const incoming = createAnalysisEntry(locale, landing, cta, search);
  if (typeof window === "undefined") return incoming;
  const params = new URLSearchParams(search);
  let prior: AnalysisEntryContext | null;
  try {
    prior =
      unavailableStorageContext ??
      readAnalysisEntry(window.sessionStorage.getItem(STORAGE_KEY) ?? "");
  } catch {
    prior = unavailableStorageContext;
  }
  const explicit =
    params.get("intent") === "analysis" || params.has("utm_source");
  let context =
    prior && !explicit ? { ...prior, locale, landing, cta } : incoming;
  if (!prior && !explicit && document.referrer) {
    try {
      const host = new URL(document.referrer).hostname;
      if (/^(www\.)?google\.(com|se)$/.test(host)) {
        context = { ...context, source: "google", medium: "organic" };
      } else if (/^(www\.)?bing\.com$/.test(host)) {
        context = { ...context, source: "bing", medium: "organic" };
      } else if (host === "linkedin.com" || host.endsWith(".linkedin.com")) {
        context = { ...context, source: "linkedin", medium: "organic_social" };
      } else if (host !== window.location.hostname) {
        context = { ...context, source: "partner", medium: "referral" };
      }
    } catch {
      /* Malformed referrers must not prevent the bounded handoff. */
    }
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      analysisEntryParams(context).toString(),
    );
    unavailableStorageContext = null;
  } catch {
    // Retain acquisition across SPA links even with sessionStorage disabled.
    unavailableStorageContext = context;
  }
  return context;
}

/** Preserve bounded acquisition parameters while keeping local navigation relative. */
export function analysisAppSignupHref(context: AnalysisEntryContext): string {
  const url = new URL(analysisSignupHref("https://app.caire.se/", context));
  return getAppHref(`${url.pathname}${url.search}${url.hash}`);
}
